/*
 * The rules the shared package states in words, as the admin pages show them -
 * in English (S48, extended to the admin site 2026-10-29).
 *
 * Each line here IS the shared function's own sentence, called rather than
 * copied, so the English cannot drift from the rule the server enforces and
 * reports. rules.da.ts says the same rules in Danish from the same constants.
 *
 * Only hints and labels live here. A validation PROBLEM the shared package
 * reports about a particular value (a file name it refuses, bytes that are not
 * what their extension says) is the server's message too, and is shown as it
 * comes - in English - like every other server message.
 */

import {
  SITE_THEME_OPTIONS,
  describeArticleImageCountLimit,
  describeArticleImageFileNameRequirement,
  describeArticleImageSizeLimit,
  describeArticlePublishingStatus,
  describeArticleStatus,
  describeArticleVisibility,
  describeAttachmentCountLimit,
  describeAttachmentFileTypes,
  describeAttachmentSizeLimit,
  describeCategoryNameRequirement,
  describeReleaseNoteImageCountLimit,
  describeReleaseNoteVersionRequirement,
  describeSiteIconSizeLimit,
  describeSlugRequirement,
  type ArticlePublishingStatus,
  type ArticleStatus,
  type ArticleVisibility,
  type SiteTheme,
} from '@accotool-kb/shared';

export const rulesEn = {
  slugRequirement: describeSlugRequirement(),
  categoryNameRequirement: describeCategoryNameRequirement(),
  imageFileNameRequirement: describeArticleImageFileNameRequirement(),
  imageSizeLimit: describeArticleImageSizeLimit(),
  articleImageCountLimit: describeArticleImageCountLimit(),
  releaseNoteImageCountLimit: describeReleaseNoteImageCountLimit(),
  /** ".pdf, .docx, ... and .webp" */
  attachmentFileTypes: describeAttachmentFileTypes(),
  attachmentSizeLimit: describeAttachmentSizeLimit(),
  attachmentCountLimit: describeAttachmentCountLimit(),
  releaseNoteVersionRequirement: describeReleaseNoteVersionRequirement(),
  siteIconSizeLimit: describeSiteIconSizeLimit(),
  /** "Public" or "Private". */
  visibility: (visibility: ArticleVisibility) => describeArticleVisibility(visibility),
  /** A theme's one-line description. Its NAME (Classic, Amber, ...) is a name, the same in both languages. */
  themeDescription: (theme: SiteTheme) =>
    SITE_THEME_OPTIONS.find((option) => option.id === theme)?.description ?? '',
  /**
   * A badge's label and tooltip: what readers see of the thing. `noun` is
   * 'article' or 'release note', as the shared function takes it.
   */
  publishingStatus: (status: ArticlePublishingStatus, noun: string = 'article') =>
    describeArticlePublishingStatus(status, noun),
  /** The two editorial gates on their own, for the publishing controls. */
  articleStatus: (status: ArticleStatus, noun: string = 'article') => describeArticleStatus(status, noun),
};
